/**
 * The shape the score chart reads: the route's points grouped by influence
 * level (platform design §11.5 forbids one axis across levels), ordered by week, with each
 * scoring-method change located.
 *
 * Nothing here computes a figure. A week the route returned no point for is
 * a row with a null `point`, so the chart breaks the line there instead of
 * joining its neighbours or dropping to the axis.
 */

import type { SentimentPoint } from '../api/schemas';

export interface SeriesPoint {
  week: string;
  score: number;
  n: number;
  low: number;
  high: number;
  version: string;
}

export interface VersionChange {
  week: string;
  from: string;
  to: string;
}

export interface LevelSeries {
  level: string | null;
  points: SeriesPoint[];
  versionChanges: VersionChange[];
}

export interface ChartRow {
  week: string;
  point: SeriesPoint | null;
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function toSeriesPoint(point: SentimentPoint): SeriesPoint {
  return {
    week: point.observed_week,
    score: point.score,
    n: point.n,
    low: point.confidence_interval[0],
    high: point.confidence_interval[1],
    version: point.scoring_model_version,
  };
}

function versionChangesOf(points: readonly SeriesPoint[]): VersionChange[] {
  const changes: VersionChange[] = [];
  points.forEach((point, index) => {
    const previous = points[index - 1];
    if (previous !== undefined && previous.version !== point.version) {
      changes.push({ week: point.week, from: previous.version, to: point.version });
    }
  });
  return changes;
}

/** One series per distinct influence level, `null` last. */
export function byLevel(points: readonly SentimentPoint[]): LevelSeries[] {
  const groups = new Map<string | null, SeriesPoint[]>();
  for (const point of points) {
    const level = point.influence_scope.level;
    groups.set(level, [...(groups.get(level) ?? []), toSeriesPoint(point)]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a === null ? 1 : b === null ? -1 : a.localeCompare(b)))
    .map(([level, unsorted]) => {
      const sorted = [...unsorted].sort((a, b) => a.week.localeCompare(b.week));
      return { level, points: sorted, versionChanges: versionChangesOf(sorted) };
    });
}

function addWeek(week: string): string {
  return new Date(Date.parse(`${week}T00:00:00Z`) + WEEK_MS).toISOString().slice(0, 10);
}

/** The series' points as chart rows, with a `point: null` row for every whole
 *  week between two consecutive points that has no reading. */
export function chartRows(series: LevelSeries): ChartRow[] {
  const rows: ChartRow[] = [];
  series.points.forEach((point, index) => {
    const previous = series.points[index - 1];
    if (previous !== undefined) {
      for (let week = addWeek(previous.week); week < point.week; week = addWeek(week)) {
        rows.push({ week, point: null });
      }
    }
    rows.push({ week: point.week, point });
  });
  return rows;
}

/** The latest point's score minus the score of the point exactly seven days
 *  before it, or `null` when the series holds no such point: a missing
 *  earlier reading is no movement to report, never a movement of zero. */
export function movementOf(series: LevelSeries): number | null {
  const latest = series.points.at(-1);
  if (latest === undefined) return null;
  const earlier = series.points.find((point) => addWeek(point.week) === latest.week);
  return earlier === undefined ? null : latest.score - earlier.score;
}

/** The scoring versions in the order they first appear. Each is drawn as its
 *  own line, so two versions are never joined into one trend. */
export function versionsOf(series: LevelSeries): string[] {
  return [...new Set(series.points.map((point) => point.version))];
}
